import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { setAuthToken } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * SSOCallback
 *
 * Landing page for Simshow SSO redirects.
 * Simshow hits:  /sso-callback#access_token=…
 *
 * Flow:
 *  1. Parse window.location.hash
 *  2. POST /auth/sso-validate  → get canonical profile from MS Graph
 *  3. Persist profile to localStorage as 'userProfileData'
 *  4. Set bearer token on the API client
 *  5. Strip hash, navigate to /
 */
const SSOCallback = () => {
  const navigate = useNavigate();
  const { ssoLogin } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      const queryParams = new URLSearchParams(window.location.search);
      const accessToken = hashParams.get("access_token");
      const environment = queryParams.get("environment") || "PROD";

      if (!accessToken) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const apiBase =
          import.meta.env.VITE_API_URL || "http://localhost:8081";

        // The backend fetches the OAuth refresh token itself, from Simshow's
        // internal-services endpoint, using this same access token — it's
        // not carried in this redirect (see /auth/sso-validate).
        const res = await fetch(`${apiBase}/auth/sso-validate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ access_token: accessToken, environment }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Validation failed (${res.status})`);
        }

        const { profile, role, permissions, access_token: plainToken } = await res.json();

        const stored = {
          // Store the plaintext MS Graph token so apiService can use it as
          // a valid Bearer token — NOT the encrypted blob from the URL.
          accessToken: plainToken || accessToken,
          loginTime: Date.now(),
          environment,
          name: profile?.name || hashParams.get("name") || "",
          email: profile?.email || hashParams.get("email") || "",
          id: profile?.id || "",
          source: "sso",
          role: role || "normal",
          permissions: permissions || {},
        };

        localStorage.setItem("userProfileData", JSON.stringify(stored));
        setAuthToken(plainToken || accessToken);
        ssoLogin(stored);

        window.history.replaceState(null, "", "/sso-callback");
        navigate("/", { replace: true });
      } catch (err) {
        console.error("[SSO] Callback error:", err);
        setError(err.message);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div style={styles.container}>
        <h2 style={styles.title}>Sign-in failed</h2>
        <p style={styles.errorText}>{error}</p>
        <button style={styles.btn} onClick={() => navigate("/")}>
          Return to login
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.spinner} />
      <p style={styles.label}>Completing sign-in…</p>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    gap: 16,
    color: "#6b7280",
  },
  spinner: {
    width: 40,
    height: 40,
    border: "3px solid #e5e7eb",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  label: { margin: 0, fontSize: 15 },
  title: { margin: 0, fontSize: 22, color: "#374151" },
  errorText: { margin: 0, color: "#ef4444" },
  btn: {
    marginTop: 8,
    padding: "8px 20px",
    border: "none",
    borderRadius: 6,
    background: "#3b82f6",
    color: "white",
    cursor: "pointer",
    fontSize: 14,
  },
};

export default SSOCallback;
